import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import LangLink from '../common/LangLink';
import { langPath } from '../../utils/langPath';
import { useLanguage } from '../../context/LanguageContext';
import { useToggleLanguage } from '../../hooks/useSwitchLanguage';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { ADMIN_ENABLED } from '../../utils/adminFeature';
import Navigation from './Navigation';

const getAdminService = () => import('../../services/adminService').then(m => m.default);
import RecommendationIcon from '../ui/icons/RecommendationIcon';
import UserAvatar from '../common/UserAvatar';

const SunIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);
const MoonIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  // Toggle qui met aussi à jour l'URL (/fr/about ↔ /en/about) pour SEO bilingue.
  const toggleLanguage = useToggleLanguage();
  const { user, logout, isAdmin, previewMode, setPreviewMode } = useAuth();
  const { theme, setTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [impersonating, setImpersonating] = useState(null); // { id, name, email }
  const [impSearchQuery, setImpSearchQuery] = useState('');
  const [impSearchResults, setImpSearchResults] = useState([]);
  const [impSearching, setImpSearching] = useState(false);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const impSearchTimer = useRef(null);

  const isDashboard = location.pathname.startsWith('/dashboard');
  const isAdminPage = location.pathname.startsWith('/admin');
  const isAppView = isDashboard || isAdminPage;

  // Check impersonation status on mount (admin only)
  useEffect(() => {
    if (!ADMIN_ENABLED || !user) return;
    getAdminService().then(svc =>
      svc.impersonateStatus()
        .then(r => setImpersonating(r.impersonating || null))
        .catch(() => {})
    );
  }, [user]);

  // Debounced user search for impersonation
  // Prefix with # to search by user ID directly (e.g. #1028)
  const handleImpSearch = (q) => {
    setImpSearchQuery(q);
    if (impSearchTimer.current) clearTimeout(impSearchTimer.current);

    // Direct ID lookup: "#123" → immediate impersonation
    if (q.startsWith('#')) {
      const idStr = q.slice(1).trim();
      if (!idStr || isNaN(idStr)) { setImpSearchResults([]); return; }
      setImpSearching(true);
      getAdminService().then(svc =>
        svc.userSearch(idStr)
          .then(r => setImpSearchResults(r.users || []))
          .catch(() => setImpSearchResults([]))
          .finally(() => setImpSearching(false))
      );
      return;
    }

    if (q.length < 2) { setImpSearchResults([]); return; }
    impSearchTimer.current = setTimeout(() => {
      setImpSearching(true);
      getAdminService().then(svc =>
        svc.userSearch(q)
          .then(r => setImpSearchResults(r.users || []))
          .catch(() => setImpSearchResults([]))
          .finally(() => setImpSearching(false))
      );
    }, 300);
  };

  const startImpersonation = async (userId) => {
    try {
      const svc = await getAdminService();
      await svc.impersonateStart(userId);
      window.location.reload();
    } catch (e) {
      console.error('Impersonation failed', e);
    }
  };

  const stopImpersonation = async () => {
    try {
      const svc = await getAdminService();
      await svc.impersonateStop();
      window.location.reload();
    } catch (e) {
      console.error('Stop impersonation failed', e);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Public site: floating dark pill header (overlays hero & sections)
  if (!isAppView) {
    return (
      <>
        <header className="fixed top-3 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-1rem)] max-w-[680px]">
          <div className="relative bg-white/95 dark:bg-black/80 backdrop-blur-md rounded-full h-[42px] md:h-auto pl-4 pr-1.5 md:pl-5 md:pr-2 md:py-2 flex items-center gap-3 border border-gray-200 dark:border-white/15 shadow-lg shadow-black/10 dark:shadow-black/20">
            {/* Logo — SVG is white-on-transparent; invert in light mode to render dark.
                Slightly smaller on mobile so the airplane in the hero remains visible above. */}
            <LangLink to="/" className="flex-shrink-0 flex items-center" onClick={() => window.scrollTo(0, 0)}>
              <img src="/img/logo-nizus.svg" alt="Nizus" className="h-5 md:h-6 w-auto invert dark:invert-0" />
            </LangLink>

            {/* Nav links — absolutely centered in the pill (independent of logo/right cluster width) */}
            <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center gap-5">
              <LangLink
                to="/levels"
                className="text-sm font-medium text-gray-700 dark:text-white/80 hover:text-black dark:hover:text-white transition"
                onClick={() => window.scrollTo(0, 0)}
              >
                {language === 'fr' ? 'Licences & qualifications' : 'Licences & Ratings'}
              </LangLink>
            </div>

            {/* Right cluster — desktop */}
            <div className="hidden md:flex items-center gap-2 ml-auto">
              <button
                onClick={toggleLanguage}
                className="px-3 py-1.5 rounded-full bg-black/[0.05] dark:bg-white/[0.08] hover:bg-black/[0.1] dark:hover:bg-white/[0.15] text-xs font-bold text-gray-700 dark:text-white/80 hover:text-black dark:hover:text-white transition"
              >
                {language === 'fr' ? 'EN' : 'FR'}
              </button>
              <button
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="p-2 rounded-full bg-black/[0.05] dark:bg-white/[0.08] hover:bg-black/[0.1] dark:hover:bg-white/[0.15] text-gray-700 dark:text-white/80 hover:text-black dark:hover:text-white transition"
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
              >
                {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
              </button>
              {user ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-5 py-2 bg-[#0061fe] hover:bg-[#0052db] text-white text-sm font-bold rounded-full transition shadow-md shadow-blue-600/30"
                >
                  {language === 'fr' ? 'Mon espace' : 'My space'}
                </button>
              ) : (
                <button
                  onClick={() => { navigate(langPath('/login', language)); window.scrollTo(0, 0); }}
                  className="px-5 py-2 bg-[#0061fe] hover:bg-[#0052db] text-white text-sm font-bold rounded-full transition shadow-md shadow-blue-600/30"
                >
                  {language === 'fr' ? 'Connexion' : 'Login'}
                </button>
              )}
            </div>

            {/* Mobile hamburger + popup dropdown */}
            <div className="md:hidden ml-auto relative" ref={mobileMenuRef}>
              <button
                type="button"
                className="p-2 text-gray-700 dark:text-white"
                onClick={toggleMobileMenu}
                aria-label="Menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                </svg>
              </button>

              {/* Popup dropdown — same style as the user menu when logged in */}
              <div className={`absolute right-0 top-full mt-2 w-60 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 transition-all duration-200 z-50 overflow-hidden ${
                mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
              }`}>
                <div className="p-2">
                  <Navigation mobile={true} onClose={toggleMobileMenu} />
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700" />

                {/* Language + Theme */}
                <div className="p-2 flex items-center justify-between">
                  <button
                    onClick={toggleLanguage}
                    className="px-3 py-1.5 rounded-md text-xs font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition"
                  >
                    {language === 'fr' ? 'EN' : 'FR'}
                  </button>
                  <button
                    onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                    className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 transition"
                    aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                  >
                    {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
                  </button>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700" />

                {/* Auth buttons */}
                <div className="p-2 flex flex-col gap-2">
                  {user ? (
                    <>
                      <button
                        className="px-4 py-2 bg-[#0061fe] hover:bg-[#0052db] text-white text-sm font-bold rounded-full transition shadow-md shadow-blue-600/30"
                        onClick={() => { navigate('/dashboard'); window.scrollTo(0, 0); toggleMobileMenu(); }}
                      >
                        {language === 'fr' ? 'Mon espace' : 'My space'}
                      </button>
                      <button
                        className="px-4 py-2 text-sm font-bold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-full transition"
                        onClick={() => { logout(); navigate(langPath('/', language)); window.scrollTo(0, 0); toggleMobileMenu(); }}
                      >
                        {language === 'fr' ? 'Déconnexion' : 'Logout'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="px-4 py-2 bg-[#0061fe] hover:bg-[#0052db] text-white text-sm font-bold rounded-full transition shadow-md shadow-blue-600/30"
                        onClick={() => { navigate(langPath('/login', language)); window.scrollTo(0, 0); toggleMobileMenu(); }}
                      >
                        {language === 'fr' ? 'Connexion' : 'Login'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Admin floating indicators */}
        {ADMIN_ENABLED && isAdmin && previewMode && (
          <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-3 py-2 bg-purple-600 text-white text-xs font-semibold rounded-full shadow-lg shadow-purple-600/30">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Preview Full Access
            <button
              onClick={async () => { try { const svc = await getAdminService(); await svc.togglePreview(); } catch(e) {} window.location.reload(); }}
              className="ml-1 p-0.5 hover:bg-purple-500 rounded-full transition"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        {ADMIN_ENABLED && impersonating && (
          <div className="fixed bottom-4 left-4 z-50 flex items-center gap-2 px-3 py-2 bg-amber-600 text-white text-xs font-semibold rounded-full shadow-lg shadow-amber-600/30">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Vu en tant que {impersonating.name} ({impersonating.email})
            <button
              onClick={stopImpersonation}
              className="ml-1 p-0.5 hover:bg-amber-500 rounded-full transition"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </>
    );
  }

  // ============== Dashboard / Admin header ==============
  return (
    <>
      <header className="fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 h-16 bg-white dark:bg-black border-b border-gray-200 dark:border-white/5 shadow-sm lg:hidden">
        <div className="w-full px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex items-center justify-between h-full gap-3 md:gap-8">

            {/* Left - Logo */}
            <div className="flex items-center gap-4 md:gap-10">
              <Link to="/dashboard" className="flex-shrink-0">
                <img
                  src="/img/logo-nizus.svg"
                  alt="logo nizus"
                  className="h-7 md:h-8 w-auto"
                  style={(theme !== 'dark') ? {
                    filter: 'brightness(0) saturate(100%) invert(19%) sepia(100%) saturate(3000%) hue-rotate(209deg) brightness(97%) contrast(105%)'
                  } : undefined}
                />
              </Link>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {/* Language & Theme — only when user popup is NOT available */}
              {!user && (
                <>
                  <button
                    onClick={toggleLanguage}
                    className="px-2.5 py-1 rounded-lg text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10 transition"
                  >
                    {language === 'fr' ? 'EN' : 'FR'}
                  </button>
                  <button
                    onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                    className="p-2 rounded-xl transition hover:bg-gray-200 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400"
                  >
                    {theme === 'dark' ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
                  </button>
                </>
              )}

              {/* User Avatar & Menu */}
              {user && (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={toggleUserMenu}
                    className="flex items-center gap-2 p-1 rounded-full transition hover:bg-gray-200 dark:hover:bg-white/10"
                  >
                    <UserAvatar size="sm" showNotificationDot />
                  </button>

                  {/* Dropdown Menu */}
                  <div className={`absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 transition-all duration-200 z-50 ${
                    userMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
                  }`}>
                    <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {user?.name || user?.email}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {user?.email}
                      </p>
                    </div>
                    <div className="p-2">
                      <Link
                        to="/dashboard/account"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {language === 'fr' ? 'Mon compte' : 'My Account'}
                      </Link>

                      {ADMIN_ENABLED && isAdmin && (
                        <>
                          <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
                          {/* App / Site switcher (admin only) */}
                          <div className="px-3 py-1.5 flex items-center gap-2">
                            <Link
                              to="/dashboard"
                              onClick={() => setUserMenuOpen(false)}
                              className={`flex-1 text-center text-xs font-bold py-1.5 rounded-lg transition-all ${
                                isDashboard
                                  ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300'
                                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              App
                            </Link>
                            <LangLink
                              to="/"
                              onClick={() => setUserMenuOpen(false)}
                              className={`flex-1 text-center text-xs font-bold py-1.5 rounded-lg transition-all ${
                                !isDashboard
                                  ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300'
                                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              Site
                            </LangLink>
                          </div>
                          <Link
                            to={isAdminPage ? "/dashboard" : "/admin"}
                            onClick={() => setUserMenuOpen(false)}
                            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10 rounded-lg transition font-medium"
                          >
                            {isAdminPage ? (
                              <>
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                                Dashboard
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Admin
                              </>
                            )}
                          </Link>
                          <button
                            onClick={async () => {
                              try {
                                const svc = await getAdminService();
                                const r = await svc.togglePreview();
                                setPreviewMode(!!r.previewMode);
                                window.location.reload();
                              } catch (e) { console.error('Toggle preview failed', e); }
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition font-medium ${
                              previewMode
                                ? 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-500/10'
                                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            {previewMode ? 'Preview Full Access ON' : 'Preview Full Access'}
                          </button>

                          {/* Impersonation search */}
                          <div className="px-3 py-2">
                            <input
                              type="text"
                              placeholder={language === 'fr' ? 'Simuler un user (#id / email)...' : 'Impersonate user (#id / email)...'}
                              value={impSearchQuery}
                              onChange={(e) => handleImpSearch(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const q = impSearchQuery.trim();
                                  if (q.startsWith('#')) {
                                    const id = parseInt(q.slice(1), 10);
                                    if (id > 0) startImpersonation(id);
                                  }
                                  else if (impSearchResults.length > 0) {
                                    startImpersonation(impSearchResults[0].id);
                                  }
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                            />
                            {impSearching && (
                              <p className="text-[10px] text-gray-400 mt-1">Recherche...</p>
                            )}
                            {impSearchResults.length > 0 && (
                              <div className="mt-1 max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md">
                                {impSearchResults.map(u => (
                                  <button
                                    key={u.id}
                                    onClick={() => startImpersonation(u.id)}
                                    className="w-full text-left px-2 py-1.5 text-xs hover:bg-amber-50 dark:hover:bg-amber-500/10 transition"
                                  >
                                    <span className="font-medium text-gray-900 dark:text-white">{u.name}</span>
                                    <span className="text-gray-500 dark:text-gray-400 ml-1">#{u.id}</span>
                                    <br />
                                    <span className="text-gray-400 dark:text-gray-500">{u.email}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      <div className="my-2 border-t border-gray-200 dark:border-gray-700" />

                      {/* Language & Theme */}
                      <div className="flex items-center justify-between px-3 py-2">
                        <button
                          onClick={toggleLanguage}
                          className="px-2.5 py-1 rounded-md text-xs font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition"
                        >
                          {language === 'fr' ? 'EN' : 'FR'}
                        </button>
                        <button
                          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                          className="p-1.5 rounded-lg transition hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400"
                        >
                          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
                        </button>
                      </div>

                      <button
                        onClick={() => {
                          logout();
                          navigate(langPath('/', language));
                          setUserMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        {language === 'fr' ? 'Déconnexion' : 'Logout'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </header>

      {/* Admin Preview Mode floating indicator */}
      {ADMIN_ENABLED && isAdmin && previewMode && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-3 py-2 bg-purple-600 text-white text-xs font-semibold rounded-full shadow-lg shadow-purple-600/30">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Preview Full Access
          <button
            onClick={async () => { try { const svc = await getAdminService(); await svc.togglePreview(); } catch(e) {} window.location.reload(); }}
            className="ml-1 p-0.5 hover:bg-purple-500 rounded-full transition"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Impersonation floating indicator */}
      {ADMIN_ENABLED && impersonating && (
        <div className="fixed bottom-4 left-4 z-50 flex items-center gap-2 px-3 py-2 bg-amber-600 text-white text-xs font-semibold rounded-full shadow-lg shadow-amber-600/30">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Vu en tant que {impersonating.name} ({impersonating.email})
          <button
            onClick={stopImpersonation}
            className="ml-1 p-0.5 hover:bg-amber-500 rounded-full transition"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </>
  );
};

export default Header;
